import path from 'node:path';
import { randomUUID } from 'node:crypto';
import camelCase from 'lodash/camelCase.js';
import { componentGenerator } from '@wix/gena';
import { getBackendAppToolsExtensionPath } from '@wix/cli-astro-definitions';
import { isStandaloneExtensionsProject } from '@wix/cli-custom-extensions';
import { modifyExtensionsEntry } from '@wix/cli-project-extender';
import { getExtensionRelativePaths } from '../utils/extension-relative-paths.js';

const APP_TOOLS_TEMPLATE = path.resolve(
  __dirname,
  '../templates/astro/app-tools'
);

interface ToolConfig {
  methodName: string;
  description: string;
  requestSchema?: Record<string, unknown>;
  responseSchema?: Record<string, unknown>;
  activated?: boolean;
}

const DEFAULT_TOOLS_CONTENT = `    {
      methodName: 'myMethod',
      description: 'Description of what this tool does',
      activated: true,
      // learn more about JSON Schema - https://json-schema.org/overview/what-is-jsonschema
      requestSchema: {
        type: 'object',
        properties: {
          param: { type: 'string' },
        },
        required: ['param'],
      },
      responseSchema: {
        type: 'object',
        properties: {
          result: { type: 'string' },
        },
      },
    },`;

function serializeSchema(schema: Record<string, unknown>): string {
  const lines = JSON.stringify(schema, null, 2).split('\n');
  return lines.map((line, i) => (i === 0 ? line : `      ${line}`)).join('\n');
}

function renderTools(tools: ToolConfig[]): string {
  return `${tools
    .map((tool) => {
      const lines: string[] = [
        `    {`,
        `      methodName: '${tool.methodName}',`,
        `      description: '${tool.description}',`,
      ];
      if (tool.activated !== undefined) {
        lines.push(`      activated: ${tool.activated},`);
      }
      if (tool.requestSchema) {
        lines.push(
          `      requestSchema: ${serializeSchema(tool.requestSchema)},`
        );
      }
      if (tool.responseSchema) {
        lines.push(
          `      responseSchema: ${serializeSchema(tool.responseSchema)},`
        );
      }
      lines.push(`    }`);
      return lines.join('\n');
    })
    .join(',\n')},`;
}

export async function generateAppTools(
  projectFolder: string,
  name: string,
  tools?: ToolConfig[]
) {
  const cwd = getBackendAppToolsExtensionPath(projectFolder, name);
  const { relativePathToConfigFile } = getExtensionRelativePaths({
    cwd,
    projectFolder,
    extensionFileName: name,
    codeFileType: 'ts',
  });

  const isCustomExtensionsProject =
    await isStandaloneExtensionsProject(projectFolder);

  const toolsContent =
    tools && tools.length > 0 ? renderTools(tools) : DEFAULT_TOOLS_CONTENT;

  const genResult = await componentGenerator({
    template: APP_TOOLS_TEMPLATE,
    cwd,
    projectFolder,
    data: {
      builderPackage: isCustomExtensionsProject
        ? '@wix/custom-extensions'
        : '@wix/astro/builders',
      name,
      config: { id: randomUUID() },
      toolsContent,
    },
  });

  await modifyExtensionsEntry({
    projectFolder,
    extensionName: camelCase(name),
    relativePath: relativePathToConfigFile,
    isCustomExtensionsProject,
  });

  return genResult;
}
